import { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { useDispatch, useSelector } from 'react-redux';
import * as S from 'containers/PropertyPanel/PropertyPanel.styles';
import type { RootState } from 'store';
import type { FlowCurvePointInterface } from './components/FlowCurve';
import { getPxxValues } from './util';
import { getActiveUnit, getActiveUnitTitle } from 'store/units/selectors';
import { useEditFlowProperties } from './useEditFlowProperties';
import PropertyPanelContent from 'containers/PropertyPanel/components/PropertyPanelContent';
import { ActionButton } from 'components/SidePanelActionBar';
import MaterialProperties from './accordions/MaterialProperties';
import PxxValues from './accordions/PxxValues';
import Fractions from './accordions/Fractions';
import SizeGradation from './accordions/SizeGradation';
import Display from './accordions/Display';
import FlowSideCard from './components/FlowSideCard';
import type { FlowPropertiesProps } from './withFlowProperties';
import { setShowFlowProperties } from 'store/units/reducer';
import theme from 'theme/theme';
import { getUnitPropertyPanelTitle } from 'containers/PropertyPanel/util/getUnitPropertyPanelTitle';

export const WrapperFlowProperties = ({
  id,
  flowProperties,
  activeUnit,
}: FlowPropertiesProps) => {
  const dispatch = useDispatch();
  const { inputPorts, currentPort } = useEditFlowProperties(
    id,
    activeUnit,
    flowProperties,
  );

  const [showCurve, setShowCurve] = useState(true);
  const activeUnitTitle = useSelector(getActiveUnitTitle);
  const unitInfoData = useSelector(
    (state: RootState) => state.unitInfoDataState.unitInfoData,
  );

  const isFeed: boolean =
    typeof inputPorts !== 'undefined' && inputPorts?.length <= 0;

  const { psdCalcMode, pxxOne, pxxTwo, pxxThree } = flowProperties;

  const [pxxPoints, setPxxPoints] = useState<FlowCurvePointInterface[]>([]);

  useEffect(() => {
    setPxxPoints(
      getPxxValues(
        pxxOne,
        pxxTwo,
        pxxThree,
        currentPort?.outputFlow,
        psdCalcMode,
      ),
    );
  }, [pxxOne, pxxTwo, pxxThree, psdCalcMode, currentPort]);

  if (!activeUnit) return null;

  const title =
    activeUnitTitle ?? getUnitPropertyPanelTitle(activeUnit, unitInfoData);

  return (
    <S.MultipleCardColumnsContainer>
      <PropertyPanelContent
        title={title}
        notes={<></>}
        displayCard={<></>}
        actionBar={
          <Stack
            direction='row'
            spacing={1}
            sx={{
              justifyContent: 'end',
              marginBottom: theme.spacing(2),
            }}
          >
            <ActionButton
              tooltip='Unit properties'
              icon={<TuneIcon sx={{ width: 18, height: 18 }} />}
              onClick={() => dispatch(setShowFlowProperties(false))}
            />
          </Stack>
        }
      >
        <MaterialProperties showCurve={showCurve} setShowCurve={setShowCurve} />
        <PxxValues pxxPoints={pxxPoints} />
        <Fractions />
        {!isFeed && <SizeGradation />}
        <Display isFeed={isFeed} />
      </PropertyPanelContent>
      {showCurve && (
        <FlowSideCard pxxPoints={pxxPoints} setShowCurve={setShowCurve} />
      )}
    </S.MultipleCardColumnsContainer>
  );
};

export const FlowProperties = ({ id }: { id: string }) => {
  const activeUnit = useSelector(getActiveUnit);
  if (!activeUnit) {
    return null;
  }

  return (
    <WrapperFlowProperties
      id={id}
      flowProperties={activeUnit.flowProperties}
      activeUnit={activeUnit}
    />
  );
};
